/**
 * Open Graph / Twitter Card для публичных карт `/public/:shareToken`.
 * Чистые (без DOM / fetch / fs) helper'ы: общие для клиентской синхронизации meta (apps/web),
 * Vite dev/preview-middleware и серверного OG-роута Fastify (apps/api).
 */

/** Совпадает с {@link useDocumentProjectTitle}: «Без названия», суффикс « — Слой». */
export function formatPublicMapDocumentTitle(projectName: string | undefined | null): string {
  const raw = typeof projectName === 'string' ? projectName.trim() : '';
  const label = raw || 'Без названия';
  return `${label} — Слой`;
}

export function escapeHtmlAttr(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

/**
 * URL JSON проекта для превью: `GET .../api/public/:shareToken`.
 * `apiBaseOrOrigin` — `http://host:port` или уже с суффиксом `/api`.
 */
export function buildPublicProjectJsonUrl(apiBaseOrOrigin: string, shareToken: string): string {
  const b = apiBaseOrOrigin.replace(/\/$/, '');
  const withApi = b.endsWith('/api') ? b : `${b}/api`;
  return `${withApi}/public/${encodeURIComponent(shareToken)}`;
}

export function buildPreviewImageUrl(
  previewUrl: string | null | undefined,
  previewUpdatedAt: string | null | undefined,
): string | null {
  if (!previewUrl?.trim()) return null;
  const u = previewUrl.trim();
  if (previewUpdatedAt?.trim()) {
    const sep = u.includes('?') ? '&' : '?';
    return `${u}${sep}v=${encodeURIComponent(previewUpdatedAt.trim())}`;
  }
  return u;
}

export interface PublicMapOpenGraphInput {
  pageTitle: string;
  canonicalUrl: string;
  imageUrl: string | null;
  /** Короткое описание для og:description (опционально). */
  description?: string | null;
}

/**
 * Фрагмент для вставки в `<head>`: og:*, twitter:*, canonical.
 * Значения должны быть уже экранированы через escapeHtmlAttr, кроме абсолютных URL (только из API / своего origin).
 */
export function buildPublicMapOpenGraphHeadFragment(input: PublicMapOpenGraphInput): string {
  const title = escapeHtmlAttr(input.pageTitle);
  const url = escapeHtmlAttr(input.canonicalUrl);
  const desc =
    typeof input.description === 'string' && input.description.trim().length > 0
      ? escapeHtmlAttr(input.description.trim().slice(0, 320))
      : null;

  const imageLine = input.imageUrl
    ? `
    <meta property="og:image" content="${escapeHtmlAttr(input.imageUrl)}" />
    <meta name="twitter:image" content="${escapeHtmlAttr(input.imageUrl)}" />`
    : '';

  const descBlock = desc
    ? `
    <meta property="og:description" content="${desc}" />
    <meta name="twitter:description" content="${desc}" />`
    : '';

  return `
    <link rel="canonical" href="${url}" />
    <meta property="og:type" content="website" />
    <meta property="og:title" content="${title}" />
    <meta property="og:url" content="${url}" />${descBlock}${imageLine}
    <meta name="twitter:card" content="${input.imageUrl ? 'summary_large_image' : 'summary'}" />
    <meta name="twitter:title" content="${title}" />
  `.trim();
}

export function replaceDocumentTitleInIndexHtml(html: string, newTitle: string): string {
  const safe = escapeHtmlAttr(newTitle);
  return html.replace(/<title>[^<]*<\/title>/i, `<title>${safe}</title>`);
}

export function injectOpenGraphBeforeClosingHead(html: string, fragment: string): string {
  const block = `\n    ${fragment}\n`;
  if (html.includes('</head>')) {
    return html.replace('</head>', `${block}</head>`);
  }
  return html;
}
